/**
 * Vanilla lightmap (LightTexture) maths, shared by the voxel / particle shaders
 * and the smoke tests.
 *
 *  - level curve: x = level/15, y = x / (4 - 3x)   (dimension ambient 0)
 *  - Brightness option ("gamma"): Moody 0 .. Bright 1, default 0.5;
 *    notGamma(v) = 1 - (1 - v)^4,  out = mix(v, notGamma(v), gamma)
 *  - block light tint: (b, b*((b*0.6+0.4)*0.6+0.4), b*(b*b*0.6+0.4)) — dim
 *    torchlight falls off warm/orange, near the source it is near white.
 */

export const DEFAULT_BRIGHTNESS = 0.5;

/** Pre-gamma floor (vanilla LightTexture lerps toward 0.75): light 0 is dark but never black. */
export const LIGHT_FLOOR_MIX = 0.02;

/** Dimension brightness: lerp(ambientLight, x/(4-3x), 1) — overworld ambient 0, Nether 0.1. */
export function lightCurve(x: number, ambient = 0): number {
  const y = x / (4 - 3 * x);
  return y + (1 - y) * ambient;
}

export function applyGamma(v: number, gamma: number): number {
  const ng = 1 - Math.pow(1 - v, 4);
  return v + (ng - v) * gamma;
}

/** Lightmap brightness for a 0..1 light fraction under the Brightness option. */
export function lightmap(x: number, gamma: number, ambient = 0): number {
  const v = lightCurve(Math.min(1, Math.max(0, x)), ambient);
  return applyGamma(v + (0.75 - v) * LIGHT_FLOOR_MIX, gamma);
}

export function blockLightTint(b: number): [number, number, number] {
  return [b, b * ((b * 0.6 + 0.4) * 0.6 + 0.4), b * (b * b * 0.6 + 0.4)];
}

/**
 * GLSL twin of the above (overworld ambient; the Nether's ambient is added
 * separately via env.uDimAmbient); expects `uniform float uGamma;` declared.
 */
export const LIGHTMAP_GLSL = /* glsl */ `
// floor + Brightness applied to an already-curved (and sky-darkened) value
float lightGamma(float y) {
  y = mix(y, 0.75, 0.02);           // floor before gamma: light 0 is dark, never black
  float ng = 1.0 - pow(1.0 - y, 4.0);
  return mix(y, ng, uGamma);
}
float lightCurve(float l) { return lightGamma(l / (4.0 - 3.0 * l)); }
// the floor part of b stays neutral grey; only real block light is warm
vec3 blockLightTint(float b) {
  float f = lightCurve(0.0);
  float w = max(b - f, 0.0) / (1.0 - f);
  return vec3(f) + (1.0 - f) * vec3(w, w * ((w * 0.6 + 0.4) * 0.6 + 0.4), w * (w * w * 0.6 + 0.4));
}
`;
